import React, { useState } from "react";
import {
  FaScroll,
  FaMobileAlt,
  FaUserCheck,
  FaHandshake,
  FaLock,
  FaTag,
  FaCreditCard,
  FaTruck,
  FaUndo,
  FaExclamationTriangle,
  FaCopyright,
  FaBalanceScale,
  FaShieldAlt,
  FaGavel,
  FaTerminal,
  FaFileContract,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaEnvelope,
  FaRegClock,
  FaBox,
  FaShoppingCart,
  FaTimes,
  FaStore,
  FaGift,
  FaChartLine,
} from "react-icons/fa";
import "../styles/TermsOfService.css";

const TermsOfService = () => {
  const [effectiveDate] = useState("03 May 2026");

  return (
    <div className="terms-container">
      {/* Header */}
      <div className="terms-header">
        <div className="header-badge">
          <h1>Terms of Service</h1>
        </div>
        <p className="effective-date">Effective Date: {effectiveDate}</p>
        <p className="tagline">
          KKB — Tradition through Generations | Lungies • Towels • Bedsheets
        </p>
      </div>

      <div className="terms-content">
        {/* Acceptance */}
        <section className="terms-section">
          <h2>
            <FaFileContract style={{ marginRight: "8px" }} /> 1. Acceptance of
            Terms
          </h2>
          <p>
            By accessing or using the KKB website, mobile application, or any
            related services (collectively, the "Platform"), you agree to be
            bound by these Terms of Service ("Terms"). If you do not agree to
            these Terms, please do not use our Platform.
          </p>
          <p>
            KKB Traditions Pvt Ltd ("KKB", "we", "us", "our") reserves the right
            to update or modify these Terms at any time without prior notice.
          </p>
        </section>

        {/* Eligibility */}
        <section className="terms-section">
          <h2>
            <FaUserCheck style={{ marginRight: "8px" }} /> 2. Eligibility
          </h2>
          <p>
            To use our Platform and purchase products (Lungies, Towels,
            Bedsheets, and other home textiles), you must:
          </p>
          <ul className="terms-list">
            <li>Be at least 18 years of age</li>
            <li>Have the legal capacity to enter into a contract</li>
            <li>Provide accurate and complete registration information</li>
            <li>Not be previously suspended or removed from our Platform</li>
          </ul>
        </section>

        {/* Account Registration */}
        <section className="terms-section">
          <h2>
            <FaLock style={{ marginRight: "8px" }} /> 3. Account Registration
          </h2>
          <p>
            To place orders, you may need to create an account. You agree to:
          </p>
          <ul className="terms-list">
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain the security of your password and account</li>
            <li>Notify us immediately of any unauthorized account use</li>
            <li>Accept responsibility for all activities under your account</li>
          </ul>
          <div className="warning-box">
            <span>
              <FaExclamationTriangle />
            </span>
            <p>
              KKB is not liable for any loss or damage from your failure to
              protect your account credentials.
            </p>
          </div>
        </section>

        {/* Products & Pricing */}
        <section className="terms-section">
          <h2>
            <FaTag style={{ marginRight: "8px" }} /> 4. Products & Pricing
          </h2>
          <p>
            KKB offers a wide range of traditional and contemporary textile
            products including:
          </p>
          <div className="product-types">
            <span className="product-type">
              <FaShoppingCart /> Lungies (Cotton, Silk, Synthetic)
            </span>
            <span className="product-type">
              <FaBox /> Bedsheets (Cotton, Satin, Printed)
            </span>
            <span className="product-type">
              <FaStore /> Towels (Bath, Hand, Kitchen)
            </span>
            <span className="product-type">
              <FaGift /> Bedcovers & Duvet Covers
            </span>
            <span className="product-type">
              <FaScroll /> Traditional Wedding Collection
            </span>
          </div>
          <p>
            <strong>Pricing:</strong> All prices are in Indian Rupees (INR) and
            include applicable taxes unless stated otherwise. We reserve the
            right to modify prices at any time without notice.
          </p>
          <p>
            <strong>Product Descriptions:</strong> We strive to display accurate
            product images and descriptions. However, slight variations in
            color, texture, or dimensions may occur due to screen settings and
            manufacturing processes.
          </p>
        </section>

        {/* Orders & Payments */}
        <section className="terms-section">
          <h2>
            <FaCreditCard style={{ marginRight: "8px" }} /> 5. Orders & Payments
          </h2>
          <div className="sub-section">
            <h3>5.1 Placing an Order</h3>
            <p>
              When you place an order, you receive an order confirmation email.
              This does not guarantee acceptance. We reserve the right to cancel
              or refuse any order for reasons including product unavailability,
              pricing errors, or suspected fraud.
            </p>
          </div>
          <div className="sub-section">
            <h3>5.2 Payment Methods</h3>
            <p>We accept multiple payment methods:</p>
            <ul className="payment-list">
              <li>
                <FaCreditCard /> Credit/Debit Cards (Visa, Mastercard, RuPay)
              </li>
              <li>
                <FaChartLine /> Net Banking (All major banks)
              </li>
              <li>
                <FaMobileAlt /> UPI (Google Pay, PhonePe, Paytm)
              </li>
              <li>
                <FaTimes /> Cash on Delivery (COD) — Available in select
                locations
              </li>
              <li>
                <FaGift /> KKB Store Credits & Gift Cards
              </li>
            </ul>
          </div>
          <div className="sub-section">
            <h3>5.3 Order Cancellation</h3>
            <p>
              You can cancel orders within the free cancellation period (30 mins
              for prepaid, 60 mins for COD). Cancellation fees may apply after
              this period. Refer to our Cancellation Policy for details.
            </p>
          </div>
        </section>

        {/* Shipping & Delivery */}
        <section className="terms-section">
          <h2>
            <FaTruck style={{ marginRight: "8px" }} /> 6. Shipping & Delivery
          </h2>
          <p>
            We ship across India. Delivery timelines typically range from 2-7
            business days depending on your location.
          </p>
          <ul className="terms-list">
            <li>
              <strong>Shipping Charges:</strong> Free shipping on orders above
              ₹499. Flat ₹49 for orders below.
            </li>
            <li>
              <strong>Delivery Attempts:</strong> Our partners will make up to 3
              delivery attempts.
            </li>
            <li>
              <strong>Delayed Delivery:</strong> We are not liable for delays
              caused by natural disasters, strikes, or courier partner issues.
            </li>
            <li>
              <strong>PO Boxes:</strong> We do not deliver to PO Box addresses.
            </li>
          </ul>
        </section>

        {/* Returns & Refunds */}
        <section className="terms-section">
          <h2>
            <FaUndo style={{ marginRight: "8px" }} /> 7. Returns & Refunds
          </h2>
          <div className="sub-section">
            <h3>7.1 Return Window</h3>
            <p>
              You can return products within 10 days of delivery for most items.
              Hygiene products (opened towels, bedsheets) are non-returnable.
            </p>
          </div>
          <div className="sub-section">
            <h3>7.2 Return Conditions</h3>
            <ul className="terms-list">
              <li>Product must be unused, unwashed, with original tags</li>
              <li>Original packaging must be intact</li>
              <li>Proof of purchase required</li>
            </ul>
          </div>
          <div className="sub-section">
            <h3>7.3 Refund Process</h3>
            <p>
              Refunds are processed within 5-7 business days after return pickup
              and quality check. Refunds go to original payment method or KKB
              credits.
            </p>
          </div>
          <div className="info-box">
            <span>
              <FaExclamationTriangle />
            </span>
            <p>
              For defective or wrong products, return shipping is free. For
              change of mind, a restocking fee may apply.
            </p>
          </div>
        </section>

        {/* User Conduct */}
        <section className="terms-section">
          <h2>
            <FaExclamationTriangle style={{ marginRight: "8px" }} /> 8. User
            Conduct
          </h2>
          <p>You agree NOT to:</p>
          <ul className="terms-list">
            <li>Use the Platform for any illegal purpose</li>
            <li>Post false, misleading, or malicious content</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Interfere with the platform's operation</li>
            <li>
              Resell products for commercial purposes without authorization
            </li>
          </ul>
        </section>

        {/* Intellectual Property */}
        <section className="terms-section">
          <h2>
            <FaCopyright style={{ marginRight: "8px" }} /> 9. Intellectual
            Property
          </h2>
          <p>
            All content on the KKB Platform — including logos, product images,
            designs, text, graphics, and software — is the property of KKB
            Traditions Pvt Ltd or its licensors and is protected by Indian and
            international copyright laws.
          </p>
          <p>
            You may not reproduce, distribute, modify, or create derivative
            works without our express written permission.
          </p>
        </section>

        {/* Limitation of Liability */}
        <section className="terms-section highlight-section">
          <h2>
            <FaBalanceScale style={{ marginRight: "8px" }} /> 10. Limitation of
            Liability
          </h2>
          <p>
            To the maximum extent permitted by law, KKB shall not be liable for:
          </p>
          <ul className="terms-list">
            <li>Indirect, incidental, or consequential damages</li>
            <li>Loss of profits, data, or business opportunities</li>
            <li>
              Delays or failures caused by circumstances beyond our control
            </li>
          </ul>
          <p>
            Our total liability shall not exceed the amount you paid for the
            product in question.
          </p>
        </section>

        {/* Indemnification */}
        <section className="terms-section">
          <h2>
            <FaShieldAlt style={{ marginRight: "8px" }} /> 11. Indemnification
          </h2>
          <p>
            You agree to indemnify and hold KKB, its officers, employees, and
            affiliates harmless from any claims, damages, losses, or expenses
            arising from:
          </p>
          <ul className="terms-list">
            <li>Your violation of these Terms</li>
            <li>Your misuse of the Platform or products</li>
            <li>Your violation of any third-party rights</li>
          </ul>
        </section>

        {/* Governing Law */}
        <section className="terms-section">
          <h2>
            <FaGavel style={{ marginRight: "8px" }} /> 12. Governing Law
          </h2>
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of India. Any disputes arising shall be subject to the
            exclusive jurisdiction of the courts in{" "}
            <strong>Coimbatore, Tamil Nadu</strong>.
          </p>
        </section>

        {/* Dispute Resolution */}
        <section className="terms-section">
          <h2>
            <FaHandshake style={{ marginRight: "8px" }} /> 13. Dispute
            Resolution
          </h2>
          <p>
            Before filing a legal claim, you agree to contact us first to
            resolve the dispute informally. If the dispute cannot be resolved
            informally, either party may submit to binding arbitration under the
            Arbitration and Conciliation Act, 1996.
          </p>
        </section>

        {/* Termination */}
        <section className="terms-section">
          <h2>
            <FaTerminal style={{ marginRight: "8px" }} /> 14. Termination
          </h2>
          <p>
            We may suspend or terminate your account and access to our Platform
            at our sole discretion, without notice, for conduct that violates
            these Terms or is harmful to other users or KKB.
          </p>
        </section>

        {/* Entire Agreement */}
        <section className="terms-section">
          <h2>
            <FaFileContract style={{ marginRight: "8px" }} /> 15. Entire
            Agreement
          </h2>
          <p>
            These Terms, along with our Privacy Policy, Cancellation Policy, and
            Shipping Policy, constitute the entire agreement between you and KKB
            regarding your use of our Platform.
          </p>
        </section>

        {/* Contact */}
        <section className="terms-section contact-section">
          <h2>
            <FaPhoneAlt style={{ marginRight: "8px" }} /> 16. Contact Us
          </h2>
          <div className="contact-details">
            <p>
              <strong>KKB Traditions Pvt Ltd</strong>
            </p>
            <p>
              <FaMapMarkerAlt /> No. 42, Heritage Complex, RS Puram
            </p>
            <p>Coimbatore - 641018, Tamil Nadu, India</p>
            <p>
              <FaEnvelope /> Email: legal@kkb.com
            </p>
            <p>
              <FaPhoneAlt /> Phone: +91-422-1234567
            </p>
            <p>
              <FaRegClock /> Support: 9 AM - 9 PM (All days)
            </p>
          </div>
          <div className="acknowledge-box">
            <p>
              By using KKB, you acknowledge that you have read, understood, and
              agree to these Terms of Service.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
